import { useMemo } from 'react';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  AutoPricingResultType,
  GroupType,
  IListingGroupItem,
  Listing,
  ListingBroadcastStatus,
  ListingGroup,
  ListingStatus,
  Marketplace,
} from 'src/WebApiController';

import { ListingWithEvent } from '../ListingTable.types';

export interface ListingGroupMetrics {
  groupName: string;
  allListings: IListingGroupItem[];
  listingGroup: ListingGroup;
  status: ListingStatus;
  metrics: {
    activeCount: number;
    soldCount: number;
    soldTickets: number;
    priceConflictCount: number;
    isAutoPricedCount: number;
    sections: string[];
    priceFloor: number | null;
    priceFloorCurrency: string | null;
    websitePriceFloor: number | null;
    sellThroughRate: number | null;
    relativeRankPremium: number | null;
    absoluteRankPremium: number | null;
  };
}

export const useListingGroupMetrics = (
  data: (ListingWithEvent | null)[] | undefined
) => {
  return useMemo(() => {
    const map = new Map<string, ListingGroupMetrics>();

    // Pre-calculate metrics for all listing groups in the data
    data?.forEach((d) => {
      // Only process listing groups
      if (!d?.isLtGrp || !d.listing) {
        return;
      }

      const listingGroup = d.listing as ListingGroup;
      const groupId = String(d.listing.id);

      // Flatten all listings in the group (including nested groups)
      const allListings = flattenListingGroup(listingGroup);
      const sellerFee = listingGroup.mkpListings?.find(
        (m) => m.mkp === Marketplace.StubHub
      )?.sellerFee;

      // Aggregate all metrics in a single pass
      const metrics = allListings.reduce(
        (acc, listing) => {
          const typedListing = listing as Listing;

          if (typedListing?.brdcstStatus === ListingBroadcastStatus.Broadcast) {
            acc.activeCount++;
          }

          if ((listing as ListingGroup)?.groupType === GroupType.FullySold) {
            acc.soldCount++;
          }

          if (
            typedListing?.isAutoPrc &&
            typedListing?.autoPrcResult ===
              AutoPricingResultType.PriceHitHardFloor
          ) {
            acc.priceConflictCount++;
          }

          if (typedListing?.isAutoPrc) {
            acc.isAutoPricedCount++;
          }

          if (typedListing.status === ListingStatus.FullySold) {
            acc.soldCount++;
          }

          const section = listing?.seating?.section;
          if (section) {
            acc.sectionsSet.add(section);
          }

          const priceFloor = typedListing?.procsFloor;
          if (priceFloor != null) {
            if (acc.minPriceFloor === null || priceFloor < acc.minPriceFloor) {
              acc.minPriceFloor = priceFloor;
              acc.minPriceFloorCurrency = typedListing?.currency ?? null;
            }
          }

          const websitePrice = typedListing?.listPrice;
          if (websitePrice != null) {
            if (
              acc.websitePriceFloor === null ||
              websitePrice < acc.websitePriceFloor
            ) {
              acc.websitePriceFloor = websitePrice;
            }
          }

          // Track total tickets and sold tickets for sell-through rate
          const quantity = typedListing?.ticketCnt ?? 0;
          const soldTickets = typedListing?.soldQty ?? 0;

          acc.totalTickets += quantity;
          acc.soldTickets += soldTickets;

          return acc;
        },
        {
          activeCount: 0,
          soldCount: 0,
          priceConflictCount: 0,
          isAutoPricedCount: 0,
          sectionsSet: new Set<string>(),
          websitePriceFloor: null as number | null,
          minPriceFloor: null as number | null,
          minPriceFloorCurrency: null as string | null,
          totalTickets: 0,
          soldTickets: 0,
        }
      );

      const sellThroughRate =
        metrics.totalTickets > 0
          ? metrics.soldTickets / metrics.totalTickets
          : null;

      const canCalculateSellerFee = sellerFee !== 1;
      map.set(groupId, {
        groupName: listingGroup.groupName || `Group ${groupId}`,
        status: listingGroup.status,
        allListings,
        listingGroup,
        metrics: {
          activeCount: metrics.activeCount,
          soldCount: metrics.soldCount,
          soldTickets: metrics.soldTickets,
          priceConflictCount: metrics.priceConflictCount,
          isAutoPricedCount: metrics.isAutoPricedCount,
          sections: Array.from(metrics.sectionsSet),
          priceFloor:
            metrics.minPriceFloor && canCalculateSellerFee
              ? metrics.minPriceFloor / (1 - (sellerFee ?? 0))
              : null,
          priceFloorCurrency: metrics.minPriceFloorCurrency,
          websitePriceFloor:
            metrics.websitePriceFloor && canCalculateSellerFee
              ? metrics.websitePriceFloor / (1 - (sellerFee ?? 0))
              : null,
          sellThroughRate,
          relativeRankPremium: listingGroup.relativeRankPremium || null,
          absoluteRankPremium: listingGroup.absoluteRankPremium || null,
        },
      });
    });
    return map;
  }, [data]);
};
